// store/store.ts
import { configureStore, combineReducers, createAction, Action } from "@reduxjs/toolkit";
import counterReducer from "./slices/counterSlice";
import membershipReducer from "./slices/membershipSlice";
import authReducer from "./slices/authSlice";
import athleteReducer from "./slices/athleteSlice";
import programReducer from "./slices/programSlice";
import navigationReducer from "./slices/navigationSlice";
import attendanceReducer from "./slices/attendanceSlice";
import settingReducer from "./slices/settingsSlice";

/**
 * Wipe every user-scoped slice. Dispatched by performLogout.
 *
 * WHY THIS EXISTS (2026-09-01)
 *
 * Logout cleared localStorage and then `navigate()`d to /login — an SPA route
 * change, NOT a page reload — so this in-memory store survived untouched. The
 * `membership` slice kept the previous user's whole record, including their
 * `stripe_customer_id`.
 *
 * The next person to sign in on that tab landed on the dashboard, where
 * MemberBillingHistory is rendered with no `membershipId` prop and falls back
 * to `selectMembershipDetails`. So they were shown, and queried against, the
 * PREVIOUS user's membership: PaymentModal read that customer id and called
 * GET /payments/stripe/payment-methods/<someone else's customer>. The
 * ownership gate answered 403 not_owner and the member simply could not pay —
 * the "I tried to pay online and it wouldn't let me" reports Chris forwards.
 * Confirmed in production: bethany.strock.1@gmail.com requesting Melissa
 * Long's Stripe customer.
 *
 * Note what the 403 was doing: without that gate they would have been shown
 * another family's saved cards. Never loosen it to make this symptom go away —
 * the bug is the stale state, and this is the fix for it.
 *
 * Resetting at the ROOT rather than clearing the membership slice on its own
 * is deliberate: every slice here is user-scoped, and the next one somebody
 * adds would otherwise reintroduce exactly this leak.
 */
export const resetAllState = createAction("app/resetAllState");

const appReducer = combineReducers({
  counter: counterReducer,
  membership: membershipReducer,
  auth: authReducer,
  athlete: athleteReducer,
  program: programReducer,
  navigation: navigationReducer,
  attendance: attendanceReducer,
  setting: settingReducer,
});

/**
 * authSlice's setUser action type, as a literal.
 *
 * NOT imported from ./slices/authSlice. That slice imports RootState back from
 * this module, so importing a VALUE from it closes a require cycle: when the
 * module graph happens to enter authSlice first, this file gets the
 * half-built module, `setUser` is still undefined, and configureStore's own
 * init dispatch throws "Cannot read properties of undefined (reading 'type')"
 * before the app renders at all. It reproduced as exactly one suite of 419
 * failing to load, so the entry order decides whether it bites.
 *
 * createSlice derives the type as `<slice name>/<reducer name>`. The test in
 * store/__tests__/identityChangeResetsStore.test.ts asserts this equals
 * setUser.type, so renaming either half fails loudly rather than silently
 * turning the reset below into dead code.
 */
const SET_USER = "auth/setUser";

/**
 * The user id carried by a setUser payload, or null when there isn't one.
 * Anything that isn't a non-empty string counts as "no identity".
 */
const userIdOf = (user: unknown): string | null => {
  const id = (user as { id?: unknown } | null | undefined)?.id;
  return typeof id === "string" && id.length > 0 ? id : null;
};

/**
 * Did a DIFFERENT person just sign in on this tab?
 *
 * Only true when the store already held a user and the incoming one has a
 * different id. A first sign-in (nothing held) has nothing to leak, and a
 * re-dispatch for the same id — App's bootstrap rehydrate, a profile refresh —
 * must not wipe the state that user is already working with.
 */
const isIdentityChange = (
  state: ReturnType<typeof appReducer> | undefined,
  action: Action
): boolean => {
  if (action.type !== SET_USER) return false;
  const previous = userIdOf(state?.auth?.userDetails);
  const next = userIdOf((action as { payload?: unknown }).payload);
  return previous !== null && next !== null && previous !== next;
};

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: Action
): ReturnType<typeof appReducer> => {
  // `undefined` makes every slice fall back to its own initialState.
  if (action.type === resetAllState.type) {
    return appReducer(undefined, action);
  }

  // A new identity on a tab that never logged out.
  //
  // WHY THIS EXISTS (2026-09-15)
  //
  // performLogout dispatches resetAllState, which closes the leak for anyone
  // who clicks "Log out". Nothing covered the other way in: a session that
  // expires, or a member who just hands over the laptop at the front desk,
  // reaches /login as an SPA route with the previous user's slices intact.
  // Signing in then dispatches setUser and nothing else — so the stale
  // `membership` survived exactly as it did before the logout fix, with the
  // same result: PaymentModal reads the previous member's stripe_customer_id,
  // the ownership gate answers 403 not_owner, and the new member cannot pay.
  //
  // It also drops `auth.permissions` / `isAdmin`, which the arriving user
  // would otherwise inherit until GET /auth/capabilities answers.
  //
  // Handled here rather than at the call sites because there are three
  // (Login, ResetPassword, AnonChatSigninField) and the next one added would
  // reintroduce the leak. The reducer sees every sign-in by construction.
  //
  // `setting` survives: it describes the CLUB, not the person (branding, club
  // name), it is refetched per tenant, and blanking it here would strip the
  // tenant's theme for the rest of the session — App fetches settings once on
  // mount and an SPA login never remounts it. Logout still clears it, via
  // clearUserData + the full reset above.
  if (isIdentityChange(state, action)) {
    const tenantScoped = { setting: state!.setting };
    return appReducer(
      { ...appReducer(undefined, action), ...tenantScoped },
      action
    );
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
